import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Mail, 
  Phone, 
  MapPin, 
  Send,
  Linkedin,
  Twitter,
  Instagram,
  CheckCircle2
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', company: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-subtle" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block px-4 py-2 bg-orange-100 text-[#F5941E] rounded-full text-sm font-semibold mb-6">
              Contact Us
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight mb-6">
              Let&apos;s Start Your{' '}
              <span className="text-gradient">Digital Journey</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed">
              Have questions or ready to get started? We&apos;d love to hear from you. 
              Our team is here to help your business grow.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="contact-form" className="py-20 md:py-32" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#F5941E]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-1">Email Us</h3>
                    <a 
                      href="mailto:contact@causaler.com" 
                      className="text-slate-600 hover:text-[#F5941E] transition-colors"
                      data-testid="contact-email"
                    >
                      contact@causaler.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#1E5E9F]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-1">Call Us</h3>
                    <p className="text-slate-600">+91 74939 06167</p>
                    <p className="text-sm text-slate-500">Mon-Fri, 9am-6pm IST</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#F5941E]" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-lg mb-1">Location</h3>
                    <p className="text-slate-600">India</p>
                    <p className="text-sm text-slate-500">Serving businesses worldwide</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div>
                <h3 className="font-heading font-semibold text-lg mb-4">Follow Us on LinkedIn</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.linkedin.com/company/causaler/" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-[#F5941E] hover:text-white text-slate-600 transition-all duration-300"
                    data-testid="contact-linkedin"
                    aria-label="LinkedIn"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  {/* <a 
                    href="#" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-[#F5941E] hover:text-white text-slate-600 transition-all duration-300"
                    data-testid="contact-twitter"
                    aria-label="Twitter"
                  >
                    <Twitter className="w-5 h-5" />
                  </a>
                  <a 
                    href="#" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center hover:bg-[#F5941E] hover:text-white text-slate-600 transition-all duration-300"
                    data-testid="contact-instagram"
                    aria-label="Instagram"
                  >
                    <Instagram className="w-5 h-5" />
                  </a> */}
                </div>
              </div>

              {/* Quick Response Promise */}
              <div className="mt-12 p-6 bg-[#F8FAFC] rounded-2xl border border-slate-100">
                <h3 className="font-heading font-semibold text-lg mb-2">Quick Response Promise</h3>
                <p className="text-slate-600 text-sm">
                  We typically respond within 24 hours. For urgent inquiries, 
                  please call us directly.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div id="contact-form2" className="bg-white p-8 md:p-10 rounded-3xl border border-slate-100 shadow-card">
              {isSubmitted ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="font-heading text-2xl font-bold mb-4">Message Sent!</h3>
                  <p className="text-slate-600 mb-6">
                    Thank you for reaching out. We&apos;ll get back to you within 24 hours.
                  </p>
                  <Button 
                    onClick={() => setIsSubmitted(false)}
                    variant="outline"
                    className="rounded-full"
                    data-testid="contact-send-another"
                  >
                    Send Another Message
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="contact-form">
                  <h3 className="font-heading text-2xl font-bold mb-6">Send us a Message</h3>
                  
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="name" className="text-slate-700 font-medium mb-2 block">
                        Your Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="John Doe"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20 bg-slate-50"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-slate-700 font-medium mb-2 block">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john@example.com"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20 bg-slate-50"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <Label htmlFor="phone" className="text-slate-700 font-medium mb-2 block">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98765 43210"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20 bg-slate-50"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company" className="text-slate-700 font-medium mb-2 block">
                        Company / Business Name
                      </Label>
                      <Input
                        id="company"
                        name="company"
                        type="text"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Your Business"
                        className="h-12 rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20 bg-slate-50"
                        data-testid="contact-company-input"
                      />
                    </div>
                  </div>

                  <div className="mb-6">
                    <Label htmlFor="message" className="text-slate-700 font-medium mb-2 block">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      required
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your business and how we can help..."
                      rows={5}
                      className="rounded-xl border-slate-200 focus:border-[#F5941E] focus:ring-[#F5941E]/20 bg-slate-50 resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-xl text-sm" data-testid="contact-error">
                      {error}
                    </div>
                  )}

                  <Button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#F5941E] hover:bg-[#D97F12] text-white rounded-full py-6 text-lg font-semibold shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="contact-submit-button"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        Send Message
                        <Send className="ml-2 w-5 h-5" />
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-[#F8FAFC]" data-testid="faq-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-[#F5941E] font-semibold text-sm tracking-wider uppercase">
              FAQ
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold mt-4">
              Frequently Asked Questions
            </h2>
          </div>

          <div className="space-y-4">
            {[
              {
                q: 'How quickly can you build my website?',
                a: 'Most websites are completed within 2-4 weeks depending on complexity. We\'ll provide a detailed timeline during our initial consultation.',
              },
              // {
              //   q: 'What does Causaler Hire cost?',
              //   a: 'Pricing varies based on your hiring volume and needs. Contact us for a customized quote that fits your budget.',
              // },
              {
                q: 'Do you offer ongoing support?',
                a: 'Yes! All our solutions come with ongoing support and maintenance options to ensure your digital presence stays up-to-date.',
              },
              {
                q: 'Can you help with an existing website?',
                a: 'Absolutely. We can redesign, optimize, or add features to your existing website.',
              },
            ].map((item, index) => (
              <div 
                key={index}
                className="bg-white p-6 rounded-2xl border border-slate-100"
                data-testid={`faq-${index}`}
              >
                <h3 className="font-heading font-semibold text-lg mb-2">{item.q}</h3>
                <p className="text-slate-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
